import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { BiBorderRadius } from 'react-icons/bi';
import { IoCloseOutline } from 'react-icons/io5';
import { PiArrowElbowUpRightFill } from 'react-icons/pi';
import './GooglePlacesAutocomplete.css';

// Declare global types for Google Maps
declare global {
  interface Window {
    google: any;
    initGoogleMaps?: () => void;
    googleMapsLoadingPromise?: Promise<void>;
  }
}

interface GooglePlacesAutocompleteProps {
  value: string;
  onChange: (value: string, placeDetails?: any) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  name?: string;
  showMapButton?: boolean;
  onPincodeExtracted?: (pincode: string) => void;
  currentLocation?: boolean;
  disabled?: boolean;
}

const GooglePlacesAutocomplete: React.FC<GooglePlacesAutocompleteProps> = ({
  value,
  onChange,
  placeholder = "Enter address",
  className = "",
  id,
  name,
  showMapButton = true,
  onPincodeExtracted,
  currentLocation = false,
  disabled = false
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const cleanupFunctionsRef = useRef<any>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const mapSearchInputRef = useRef<HTMLInputElement>(null);
  const mapSearchAutocompleteRef = useRef<any>(null);
  const mapSearchServiceRef = useRef<any>(null);
  const mapSearchDebounceRef = useRef<number | null>(null);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [mapSearchValue, setMapSearchValue] = useState('');
  const [mapPredictions, setMapPredictions] = useState<any[]>([]);
  const [showMapPredictions, setShowMapPredictions] = useState(false);
  const onChangeRef = useRef(onChange);

  // Custom react autocompletion for the main input
  const [lastSelectedLocation, setLastSelectedLocation] = useState(value);
  const [localValue, setLocalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const [mainPredictions, setMainPredictions] = useState<any[]>([]);
  const [showMainPredictions, setShowMainPredictions] = useState(false);
  const mainSearchDebounceRef = useRef<number | null>(null);
  const mainSearchServiceRef = useRef<any>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [originalPlaceDetails, setOriginalPlaceDetails] = useState<any>(null);

  // Sync states when value changes externally (not focused)
  useEffect(() => {
    console.log("GPA: Sync useEffect - isFocused:", isFocused, "value:", value, "localValue:", localValue);
    if (!isFocused) {
      setLocalValue(value);
      if (value) {
        setLastSelectedLocation(value);
        // Geocode to fetch and store details for the externally set value
        if (window.google?.maps?.Geocoder) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ address: value }, (results: any, status: any) => {
            console.log("GPA: Geocoding value from external sync - status:", status, "results count:", results?.length);
            if (status === 'OK' && results?.[0]) {
              setOriginalPlaceDetails(results[0]);
            }
          });
        }
      }
    }
  }, [value, isFocused]);

  // Generate unique IDs for this component instance to track pac-containers
  const instanceIdRef = useRef(`gpa-${Math.random().toString(36).substring(2, 11)}`);
  const mainInputPacContainerRef = useRef<HTMLElement | null>(null);
  const mapInputPacContainerRef = useRef<HTMLElement | null>(null);

  // Keep onChange ref updated without triggering re-initialization
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Cleanup pac-containers on component unmount
  useEffect(() => {
    return () => {
      // Remove all pac-containers associated with this component instance
      const inputId = inputRef.current?.id || `gpa-main-${instanceIdRef.current}`;
      const pacContainers = document.querySelectorAll('.pac-container');
      pacContainers.forEach((container: any) => {
        const dataInputId = container.getAttribute('data-input-id');
        if (dataInputId === inputId) {
          container.remove();
        }
      });

      // Also clean up any orphaned pac-containers without data-input-id
      // (these might be left over from Google Maps)
      setTimeout(() => {
        const allPacContainers = document.querySelectorAll('.pac-container');
        allPacContainers.forEach((container: any) => {
          // Check if the container's associated input still exists in the DOM
          const dataInputId = container.getAttribute('data-input-id');
          if (dataInputId) {
            const associatedInput = document.getElementById(dataInputId);
            if (!associatedInput) {
              // Input is gone, remove the pac-container
              container.remove();
            }
          }
        });

        // Clean up any Google Maps attribution/branding elements that might be left behind
        const gmnoprint = document.querySelectorAll('.gmnoprint');
        gmnoprint.forEach((el: any) => {
          // Only remove if it's not inside an active map container
          const isInsideMap = el.closest('#map-container');
          if (!isInsideMap) {
            el.remove();
          }
        });

        // Clean up gm-style elements that are not inside a map
        const gmStyle = document.querySelectorAll('.gm-style');
        gmStyle.forEach((el: any) => {
          const isInsideMap = el.closest('#map-container');
          if (!isInsideMap && !el.querySelector('#map-container')) {
            el.remove();
          }
        });
      }, 100);
    };
  }, []);

  // Ensure input has an ID for tracking pac-containers
  useEffect(() => {
    if (inputRef.current && !inputRef.current.id) {
      inputRef.current.id = `gpa-main-${instanceIdRef.current}`;
    }
  }, []);

  // Helper to show only the pac-container for a specific input
  const showPacContainerForInput = (inputElement: HTMLInputElement) => {
    const allPacContainers = document.querySelectorAll('.pac-container');
    allPacContainers.forEach((container: any) => {
      const dataInputId = container.getAttribute('data-input-id');
      if (dataInputId === inputElement.id) {
        container.style.display = 'block';
        container.style.visibility = 'visible';
      } else {
        container.style.display = 'none';
      }
    });
  };

  // Helper to hide pac-container for a specific input
  const hidePacContainerForInput = (inputElement: HTMLInputElement) => {
    const allPacContainers = document.querySelectorAll('.pac-container');
    allPacContainers.forEach((container: any) => {
      const dataInputId = container.getAttribute('data-input-id');
      if (dataInputId === inputElement.id) {
        container.style.display = 'none';
      }
    });
  };

  // Get user's current location for location bias
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.warn('Could not get user location for address bias:', error);
          // Continue without location bias
        },
        {
          enableHighAccuracy: false,
          timeout: 5000,
          maximumAge: 300000 // Cache for 5 minutes
        }
      );
    }
  }, []);

  // Load Google Maps script (singleton pattern)
  const loadGoogleMapsScript = useCallback(() => {
    // Return existing promise if already loading
    if (window.googleMapsLoadingPromise) {
      return window.googleMapsLoadingPromise;
    }

    // Check if already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      return Promise.resolve();
    }

    // Create new loading promise
    window.googleMapsLoadingPromise = new Promise<void>((resolve, reject) => {
      // Check if script is already in DOM
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        const handleLoad = () => {
          existingScript.removeEventListener('load', handleLoad);
          existingScript.removeEventListener('error', handleError);
          resolve();
        };
        const handleError = () => {
          existingScript.removeEventListener('load', handleLoad);
          existingScript.removeEventListener('error', handleError);
          reject(new Error('Failed to load Google Maps'));
        };
        existingScript.addEventListener('load', handleLoad);
        existingScript.addEventListener('error', handleError);
        return;
      }

      // Get API key
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

      if (!apiKey) {
        const error = 'Google Maps API key is missing. Please add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file';
        console.error('❌', error);
        reject(new Error(error));
        return;
      }

      // Create and load the script
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGoogleMaps`;
      script.async = true;
      script.defer = true;

      window.initGoogleMaps = () => {
        delete window.googleMapsLoadingPromise;
        resolve();
      };

      script.onerror = () => {
        delete window.googleMapsLoadingPromise;
        reject(new Error('Failed to load Google Maps script. Check your API key and network connection.'));
      };

      document.head.appendChild(script);
    });

    return window.googleMapsLoadingPromise;
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initializeGooglePlaces = async () => {
      try {
        setLoadError(null);
        await loadGoogleMapsScript();

        if (isMounted) {
          setIsGoogleMapsLoaded(true);
        }
      } catch (error: any) {
        if (isMounted) {
          console.error('❌ Error initializing Google Places:', error);
          setLoadError(error.message || 'Failed to load Google Places');
          setIsGoogleMapsLoaded(false);
        }
      }
    };

    initializeGooglePlaces();

    return () => {
      isMounted = false;
    };
  }, [loadGoogleMapsScript]);

  const triggerMainSearch = (newValue: string) => {
    if (!window.google?.maps?.places) return;

    if (!mainSearchServiceRef.current) {
      mainSearchServiceRef.current = new window.google.maps.places.AutocompleteService();
    }

    if (mainSearchDebounceRef.current) {
      window.clearTimeout(mainSearchDebounceRef.current);
    }

    const query = newValue.trim();
    if (query.length < 2) {
      setMainPredictions([]);
      return;
    }

    mainSearchDebounceRef.current = window.setTimeout(() => {
      const options: any = {
        input: query,
        componentRestrictions: { country: 'IN' },
        types: ['geocode', 'establishment'],
      };

      if (userLocation && window.google?.maps?.LatLng) {
        options.location = new window.google.maps.LatLng(userLocation.lat, userLocation.lng);
        options.radius = 5000;
      }

      mainSearchServiceRef.current.getPlacePredictions(
        options,
        (predictions: any[] | null, status: any) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions?.length) {
            setMainPredictions(predictions);
          } else {
            setMainPredictions([]);
          }
        }
      );
    }, 180);
  };

  const handleMainPredictionSelect = (prediction: any) => {
    console.log("GPA: handleMainPredictionSelect called - prediction:", prediction);
    if (!prediction?.place_id || !window.google?.maps) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ placeId: prediction.place_id }, (results: any, status: any) => {
      console.log("GPA: geocode response for prediction - status:", status, "description:", prediction.description);
      setShowMainPredictions(false);
      setIsFocused(false);

      if (status !== 'OK' || !results?.[0]) {
        console.error('Place lookup failed:', status);
        setLocalValue(prediction.description);
        setLastSelectedLocation(prediction.description);
        onChangeRef.current(prediction.description);
        return;
      }

      const place = results[0];
      setLocalValue(prediction.description);
      setLastSelectedLocation(prediction.description);
      setOriginalPlaceDetails(place);
      onChangeRef.current(prediction.description, place);

      if (onPincodeExtracted) {
        const pincode = extractPincode(place);
        onPincodeExtracted(pincode || '');
      }
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChangeRef.current(newValue);

    if (newValue.trim().length === 0) {
      setMainPredictions([]);
      return;
    }

    triggerMainSearch(newValue);
  };

  // Click outside listener to close the main autocomplete dropdown
  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      console.log("GPA: handleDocumentClick triggered - target tag:", target.tagName, "target class:", target.className);
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        inputRef.current &&
        !inputRef.current.contains(target)
      ) {
        console.log("GPA: click outside detected! Closing dropdown and restoring. lastSelectedLocation:", lastSelectedLocation);
        setShowMainPredictions(false);
        setIsFocused(false);
        // Restore last selected location if input is empty or they didn't select anything
        if (inputRef.current) {
          const currentVal = inputRef.current.value.trim();
          console.log("GPA: click outside - input currentVal:", currentVal, "lastSelectedLocation:", lastSelectedLocation);
          if (!currentVal || currentVal !== lastSelectedLocation) {
            setLocalValue(lastSelectedLocation);
            onChangeRef.current(lastSelectedLocation, originalPlaceDetails);
          }
        }
      } else {
        console.log("GPA: click was INSIDE dropdown/input. No restore.");
      }
    };
    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, [lastSelectedLocation, originalPlaceDetails]);

  const handleMapPredictionSelect = useCallback((prediction: any) => {
    if (!prediction?.place_id || !window.google?.maps) return;

    setMapSearchValue(prediction.description || '');
    setMapPredictions([]);
    setShowMapPredictions(false);

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ placeId: prediction.place_id }, (results: any, status: any) => {
      if (status !== 'OK' || !results?.[0]) {
        console.error('Place lookup failed:', status);
        return;
      }

      const place = results[0];
      const geometryLocation = place.geometry?.location;
      if (!geometryLocation) return;

      const newLocation = {
        lat: geometryLocation.lat(),
        lng: geometryLocation.lng()
      };

      setSelectedLocation(newLocation);
      setMapSearchValue(place.formatted_address || prediction.description || '');

      if (mapRef.current) {
        mapRef.current.setCenter(newLocation);
        mapRef.current.setZoom(15);
      }

      if (markerRef.current) {
        markerRef.current.setPosition(newLocation);
      }
    });
  }, []);

  // Extract pincode from place details
  const extractPincode = (place: any): string | null => {
    if (!place || !place.address_components) return null;

    // Look for postal_code in address components
    for (const component of place.address_components) {
      if (component.types.includes('postal_code')) {
        return component.long_name;
      }
    }

    return null;
  };

  // Geocode address to get coordinates
  const geocodeAddress = async (address: string): Promise<{ lat: number; lng: number } | null> => {
    if (!address || !window.google?.maps) return null;

    try {
      const geocoder = new window.google.maps.Geocoder();
      const result = await new Promise<any>((resolve, reject) => {
        geocoder.geocode({ address }, (results: any, status: any) => {
          if (status === 'OK' && results && results[0]) {
            resolve(results[0]);
          } else {
            reject(new Error('Geocoding failed'));
          }
        });
      });

      return {
        lat: result.geometry.location.lat(),
        lng: result.geometry.location.lng()
      };
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  // Open map modal
  const handleOpenMap = async () => {
    if (!isGoogleMapsLoaded) {
      alert('Google Maps is still loading. Please wait a moment.');
      return;
    }

    // Disable main input autocomplete
    if (autocompleteRef.current) {
      autocompleteRef.current.setOptions({ disabled: true });
    }

    // Hide main input's pac-container only
    if (inputRef.current) {
      hidePacContainerForInput(inputRef.current);
    }

    // Set initial search value to current address
    setMapSearchValue(value || '');

    // Try to geocode current address
    let location = selectedLocation;
    if (value && !location) {
      location = await geocodeAddress(value);
    }

    // Fallback to user location or default
    if (!location) {
      location = userLocation || { lat: 28.6139, lng: 77.2090 }; // Default to Delhi
    }

    setSelectedLocation(location);
    setShowMapModal(true);

    // Initialize map after modal is shown
    setTimeout(() => {
      initializeMap(location!);
    }, 100);
  };

  // Initialize Google Map in modal
  const initializeMap = (location: { lat: number; lng: number }) => {
    const mapElement = document.getElementById('map-container');
    if (!mapElement || !window.google?.maps) return;

    // Create map
    mapRef.current = new window.google.maps.Map(mapElement, {
      center: location,
      zoom: 15,
      mapTypeControl: false,
      streetViewControl: true,
      fullscreenControl: false,
    });

    // Create draggable marker
    markerRef.current = new window.google.maps.Marker({
      position: location,
      map: mapRef.current,
      draggable: true,
      animation: window.google.maps.Animation.DROP,
    });

    // Update location when marker is dragged
    markerRef.current.addListener('dragend', async () => {
      const position = markerRef.current.getPosition();
      const newLocation = {
        lat: position.lat(),
        lng: position.lng()
      };
      setSelectedLocation(newLocation);

      // Reverse geocode to get address and update search input
      try {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: newLocation }, (results: any, status: any) => {
          if (status === 'OK' && results && results[0]) {
            setMapSearchValue(results[0].formatted_address);
          }
        });
      } catch (error) {
        console.error('Reverse geocoding error:', error);
      }
    });

    // Allow clicking on map to move marker
    mapRef.current.addListener('click', (e: any) => {
      const newLocation = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng()
      };
      markerRef.current.setPosition(e.latLng);
      setSelectedLocation(newLocation);

      // Reverse geocode and update search input
      try {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: newLocation }, (results: any, status: any) => {
          if (status === 'OK' && results && results[0]) {
            setMapSearchValue(results[0].formatted_address);
          }
        });
      } catch (error) {
        console.error('Reverse geocoding error:', error);
      }
    });        // Map search uses AutocompleteService (see useEffect for mapSearchValue)
        // No need for a competing google.maps.places.Autocomplete widget here.
  };

  // Cleanup map search autocomplete when modal closes
  const cleanupMapSearch = () => {
    if (mapSearchDebounceRef.current) {
      window.clearTimeout(mapSearchDebounceRef.current);
      mapSearchDebounceRef.current = null;
    }
    setMapPredictions([]);
    setShowMapPredictions(false);

    if (mapSearchAutocompleteRef.current) {
      // Clear event listeners
      if (window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(mapSearchAutocompleteRef.current);
      }

      mapSearchAutocompleteRef.current = null;
    }

    // Clean up map instance
    if (mapRef.current && window.google?.maps?.event) {
      window.google.maps.event.clearInstanceListeners(mapRef.current);
      mapRef.current = null;
    }

    // Clean up marker
    if (markerRef.current && window.google?.maps?.event) {
      window.google.maps.event.clearInstanceListeners(markerRef.current);
      markerRef.current.setMap(null);
      markerRef.current = null;
    }

    // Re-enable main input autocomplete
    if (autocompleteRef.current) {
      autocompleteRef.current.setOptions({ disabled: false });
    }

    // Remove the map search pac-container completely
    if (mapSearchInputRef.current) {
      const inputId = mapSearchInputRef.current.id;
      const pacContainers = document.querySelectorAll('.pac-container');
      pacContainers.forEach((container: any) => {
        const dataInputId = container.getAttribute('data-input-id');
        if (dataInputId === inputId) {
          container.remove();
        }
      });
    }

    // Clear the map container content
    const mapContainer = document.getElementById('map-container');
    if (mapContainer) {
      mapContainer.innerHTML = '';
    }
  };

  // Update handleConfirmLocation to cleanup
  const handleConfirmLocationWithCleanup = async () => {
    await handleConfirmLocation();
    cleanupMapSearch();
  };
  // Geocode address to get coordinates (This function should be defined earlier)
  // ...

  // Handle using current location (GPS-based)
  const handleUseCurrentLocation = () => {
    // 1. Basic checks
    // if (!isGoogleMapsLoaded || !window.google?.maps) {
    //     alert('Google Maps is still loading. Please wait a moment.');
    //     return;
    // }

    // if (!navigator.geolocation) {
    //     alert('Geolocation is not supported by your browser.');
    //     return;
    // }

    // 2. Fetch current position
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };

        // 3. Reverse geocode to get the full address
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: newLocation }, (results: any, status: any) => {
          if (status === 'OK' && results && results[0]) {
            const place = results[0];

            // Update the main form field using the formatted address and place details
            onChangeRef.current(place.formatted_address, place);

            // Extract and callback pincode if handler is provided
            if (onPincodeExtracted) {
              const pincode = extractPincode(place);
              onPincodeExtracted(pincode || '');
            }

          } else {
            console.error('Reverse Geocoding failed:', status);
            alert('Could not retrieve address from current location.');
          }
        });

      },
      (error) => {
        // Handle errors like permission denied or timeout
        console.error('Geolocation Error:', error);
        let errorMessage = 'Failed to get your current location.';
        if (error.code === error.PERMISSION_DENIED) {
          errorMessage += ' Please enable location services for this site.';

        }
        else if (error.code === error.PERMISSION_DENIED) {
          errorMessage += ' Please enable location services for this site.';

        } else if (error.code === error.TIMEOUT) {
          errorMessage += ' The request timed out.';
        }
        alert(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    );
  };

  useEffect(() => {
    if (currentLocation == true) {
      handleUseCurrentLocation()
    }
  }, [currentLocation])

  useEffect(() => {
    if (!showMapModal || !window.google?.maps?.places) {
      setMapPredictions([]);
      setShowMapPredictions(false);
      return;
    }

    if (!mapSearchServiceRef.current) {
      mapSearchServiceRef.current = new window.google.maps.places.AutocompleteService();
    }

    const query = mapSearchValue.trim();
    if (mapSearchDebounceRef.current) {
      window.clearTimeout(mapSearchDebounceRef.current);
    }

    if (query.length < 2) {
      setMapPredictions([]);
      setShowMapPredictions(false);
      return;
    }

    if (document.activeElement !== mapSearchInputRef.current) {
      setShowMapPredictions(false);
      return;
    }

    mapSearchDebounceRef.current = window.setTimeout(() => {
      mapSearchServiceRef.current.getPlacePredictions(
        {
          input: query,
          componentRestrictions: { country: 'IN' },
          types: ['geocode', 'establishment'],
        },
        (predictions: any[] | null, status: any) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions?.length) {
            setMapPredictions(predictions);
            setShowMapPredictions(true);
          } else {
            setMapPredictions([]);
            setShowMapPredictions(false);
          }
        }
      );
    }, 180);

    return () => {
      if (mapSearchDebounceRef.current) {
        window.clearTimeout(mapSearchDebounceRef.current);
        mapSearchDebounceRef.current = null;
      }
    };
  }, [mapSearchValue, showMapModal]);

  const handleUseCurrentLocationFromMap = () => {
    // 1. Call the existing geolocation/reverse geocoding logic
    handleUseCurrentLocation(); // Assumes handleUseCurrentLocation is accessible and updates the main input

    // 2. Close the map modal and cleanup
    cleanupMapSearch();
    setShowMapModal(false);
  };

  // Confirm location from map
  const handleConfirmLocation = async () => {
    if (!selectedLocation) return;

    // Reverse geocode to get address
    try {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: selectedLocation }, (results: any, status: any) => {
        if (status === 'OK' && results && results[0]) {
          onChangeRef.current(results[0].formatted_address, results[0]);

          // Extract and callback pincode if handler is provided
          if (onPincodeExtracted) {
            const pincode = extractPincode(results[0]);
            // Always call the callback, even if pincode is null
            onPincodeExtracted(pincode || '');
          }

          setShowMapModal(false);
        } else {
          alert('Could not get address for this location');
        }
      });
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      alert('Failed to get address for this location');
    }
  };

  return (
    <>
      <style>{`
        /* Google Places Autocomplete Dropdown Styles */
        .pac-container {
          background-color: #fff !important;
          z-index: 99999 !important;
          border-radius: 4px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
        }

        /* Higher z-index for map modal search */
        .pac-container[data-input-id="map-search-input"] {
          z-index: 100001 !important;
        }

        .pac-container:after {
          display: none !important;
        }

        .pac-item {
          cursor: pointer;
          padding: 10px 14px;
          font-size: 14px;
          line-height: 1.5;
          border-top: 1px solid #e6e6e6;
          background-color: #fff;
        }

        .pac-item:first-child {
          border-top: none;
        }

        .pac-item:hover {
          background-color: #f5f5f5;
        }

        .pac-item-selected,
        .pac-item-selected:hover {
          background-color: #ebf5ff;
        }

        .pac-icon {
          width: 15px;
          height: 20px;
          margin-right: 7px;
          margin-top: 6px;
          display: inline-block;
          vertical-align: top;
          background-image: url(https://maps.gstatic.com/mapfiles/api-3/images/autocomplete-icons.png);
          background-size: 34px;
        }

        .pac-item-query {
          font-size: 14px;
          color: #333;
          font-weight: 500;
        }

        .pac-matched {
          font-weight: 700;
        }

        .pac-logo:after {
          content: "";
          padding: 1px 1px 1px 0;
          height: 16px;
          text-align: right;
          display: block;
          background-image: url(https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3.png);
          background-position: right;
          background-repeat: no-repeat;
          background-size: 120px 14px;
        }
      `}</style>


      <div className='basic_field_inner' style={{
        position: 'relative',
        width: '100%',
        border: '0',
        // backgroundColor: '#F8F8F8',
        borderRadius: '10px',


      }}>
        <input
          ref={inputRef}
          type="text"
          value={localValue}
          onChange={handleInputChange}
          onFocus={() => {
            if (disabled) return;
            setIsFocused(true);
            setShowMainPredictions(true);
            if (value) {
              setLastSelectedLocation(value);
              triggerMainSearch(value);
            }
            setLocalValue("");
          }}
          placeholder={placeholder}
          className={className || ""}
          id={id}
          name={name}
          autoComplete="off"
          disabled={disabled}
        />
        {showMapButton && isGoogleMapsLoaded && !disabled && (
          <button
            type="button"
            onClick={handleOpenMap}
            style={{
              position: 'absolute',
              right: '20px',
              top: '60%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#0066cc',
              zIndex: 2,
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#0052a3'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#0066cc'}
            title="Open map to select location"
          >
            <svg width="22" height="23" viewBox="0 0 22 23" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.7038 16.6914C18.3025 15.5772 18.5268 14.3 18.3435 13.0485C18.1602 11.797 17.5791 10.6376 16.686 9.74193C16.6759 9.73143 16.6643 9.7228 16.6538 9.71268V3.0118C16.6538 2.94208 16.6343 2.87373 16.5976 2.81444C16.5609 2.75516 16.5084 2.70728 16.446 2.67618L11.1446 0.0391779C11.1353 0.0346779 11.1251 0.0331779 11.1154 0.0294279C11.1007 0.0229425 11.0855 0.0175528 11.07 0.0133029C11.0555 0.00989464 11.0409 0.00738981 11.0261 0.00580294C11.0094 0.00329557 10.9925 0.00191749 10.9755 0.00167797C10.9616 0.00185491 10.9477 0.00298256 10.9339 0.00505299C10.9165 0.00671126 10.8994 0.00972038 10.8825 0.014053C10.8684 0.0179714 10.8547 0.0228581 10.8413 0.028678C10.8307 0.0316133 10.8203 0.0351184 10.8101 0.0391779L5.67638 2.59293L0.541879 0.0391779C0.484578 0.0107039 0.420945 -0.00263485 0.357034 0.000430695C0.293122 0.00349624 0.231058 0.0228641 0.176746 0.0566917C0.122434 0.0905192 0.0776805 0.137681 0.0467442 0.193691C0.0158079 0.2497 -0.000282541 0.312693 3.75475e-06 0.376678V15.3309C-0.000309137 15.401 0.0190243 15.4698 0.055811 15.5294C0.0925977 15.5891 0.145365 15.6372 0.208129 15.6684L5.50913 18.3017C5.51475 18.3043 5.52113 18.3017 5.52713 18.3058C5.57375 18.3289 5.62509 18.341 5.67713 18.341C5.72917 18.341 5.78051 18.3289 5.82713 18.3058C5.83275 18.3032 5.83875 18.3058 5.84438 18.3017L7.767 17.3473C8.60153 18.5025 9.8275 19.3151 11.2166 19.6336C12.6056 19.9521 14.0631 19.7549 15.3176 19.0787L17.7255 21.8379C17.9006 22.0378 18.1148 22.1998 18.3548 22.314C18.5948 22.4281 18.8557 22.492 19.1213 22.5017H19.1846C19.6989 22.501 20.1919 22.2967 20.556 21.9336C20.7452 21.7463 20.8935 21.5218 20.9915 21.2742C21.0894 21.0267 21.1348 20.7615 21.1249 20.4954C21.1157 20.2298 21.0523 19.9689 20.9385 19.7287C20.8246 19.4885 20.6628 19.2742 20.463 19.0989L17.7038 16.6914ZM15.9038 9.09168C15.2492 8.63211 14.508 8.31065 13.7251 8.14689C12.9423 7.98313 12.1344 7.98051 11.3505 8.13918V0.981928L15.9019 3.2443L15.9038 9.09168ZM0.750004 0.981928L5.30138 3.2443V17.3608L0.750004 15.0984V0.981928ZM6.05138 17.3616V3.2443L10.6005 0.981928V8.34955C9.79235 8.62657 9.0545 9.07683 8.43856 9.66884C7.82263 10.2608 7.3435 10.9803 7.03471 11.7768C6.72591 12.5734 6.5949 13.4278 6.65086 14.2803C6.70681 15.1327 6.94837 15.9627 7.35863 16.7121L6.05138 17.3616ZM8.89613 17.5318C8.1782 16.8139 7.68929 15.8992 7.49121 14.9034C7.29313 13.9077 7.39478 12.8755 7.78331 11.9375C8.17184 10.9995 8.8298 10.1978 9.67398 9.63369C10.5182 9.06962 11.5107 8.76855 12.5259 8.76855C13.5412 8.76855 14.5337 9.06962 15.3779 9.63369C16.2221 10.1978 16.88 10.9995 17.2686 11.9375C17.6571 12.8755 17.7588 13.9077 17.5607 14.9034C17.3626 15.8992 16.8737 16.8139 16.1558 17.5318C15.1922 18.4928 13.8868 19.0325 12.5259 19.0325C11.165 19.0325 9.8597 18.4928 8.89613 17.5318ZM20.0258 21.4033C19.9115 21.5195 19.7742 21.6106 19.6227 21.6706C19.4712 21.7306 19.3088 21.7582 19.146 21.7517C18.9826 21.7468 18.822 21.7081 18.6743 21.638C18.5266 21.5679 18.3951 21.468 18.288 21.3444L15.9566 18.6733C16.4788 18.3084 16.9326 17.8543 17.2973 17.3319L19.968 19.6633C20.091 19.7707 20.1907 19.9022 20.2607 20.0497C20.3307 20.1973 20.3696 20.3576 20.375 20.5208C20.3803 20.684 20.3521 20.8466 20.2919 20.9984C20.2318 21.1502 20.1414 21.2881 20.0258 21.4033Z" fill="black" />
              <path d="M15.4441 10.9835C14.867 10.4072 14.132 10.0149 13.332 9.85619C12.5319 9.69749 11.7028 9.77951 10.9494 10.0919C10.196 10.4043 9.5521 10.933 9.09909 11.6112C8.64608 12.2894 8.4043 13.0867 8.4043 13.9023C8.4043 14.718 8.64608 15.5153 9.09909 16.1935C9.5521 16.8717 10.196 17.4004 10.9494 17.7128C11.7028 18.0252 12.5319 18.1072 13.332 17.9485C14.132 17.7898 14.867 17.3975 15.4441 16.8212C16.2169 16.0463 16.6509 14.9967 16.6509 13.9023C16.6509 12.808 16.2169 11.7584 15.4441 10.9835ZM14.9124 16.2909C14.4401 16.7623 13.8388 17.0832 13.1842 17.2129C12.5297 17.3427 11.8514 17.2755 11.235 17.0199C10.6187 16.7643 10.0919 16.3317 9.72134 15.7768C9.35075 15.2219 9.15296 14.5696 9.15296 13.9023C9.15296 13.2351 9.35075 12.5828 9.72134 12.0279C10.0919 11.473 10.6187 11.0404 11.235 10.7848C11.8514 10.5292 12.5297 10.462 13.1842 10.5918C13.8388 10.7215 14.4401 11.0424 14.9124 11.5138C15.5447 12.1479 15.8999 13.0068 15.8999 13.9023C15.8999 14.7979 15.5447 15.6568 14.9124 16.2909Z" fill="black" />
            </svg>
          </button>
        )}

        {showMainPredictions && (lastSelectedLocation || mainPredictions.length > 0) && (
          <div
            ref={dropdownRef}
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: '#fff',
              border: '1px solid var(--ajm-gray-200, #e2e8f0)',
              borderRadius: '8px',
              marginTop: '4px',
              maxHeight: '260px',
              overflowY: 'auto',
              zIndex: 99999,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              padding: '6px 0',
            }}
          >
            {lastSelectedLocation && (
              <div
                onClick={() => {
                  setShowMainPredictions(false);
                }}
                style={{
                  padding: '10px 14px',
                  cursor: 'pointer',
                  backgroundColor: '#ebf5ff',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: 600,
                  color: 'var(--ajm-primary, #0066cc)',
                }}
              >
                <span style={{ flex: 1, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                  {lastSelectedLocation}
                </span>
              </div>
            )}

            {mainPredictions
              .filter(pred => pred.description !== lastSelectedLocation)
              .map((prediction) => (
                <div
                  key={prediction.place_id}
                  onClick={() => handleMainPredictionSelect(prediction)}
                  style={{
                    padding: '10px 14px',
                    cursor: 'pointer',
                    color: '#1E293B',
                    fontSize: '14px',
                    lineHeight: 1.4,
                    borderBottom: '1px solid #f1f5f9',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {prediction.description}
                </div>
              ))}
          </div>
        )}
        {loadError && (
          <div style={{
            marginTop: '4px',
            fontSize: '12px',
            color: '#dc3545',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <span>⚠️</span>
            <span>Google Places unavailable. You can still enter address manually.</span>
          </div>
        )}
        {!isGoogleMapsLoaded && !loadError && (
          <div style={{
            marginTop: '4px',
            fontSize: '12px',
            color: '#6c757d',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <span>Loading address suggestions...</span>
          </div>
        )}
      </div >

      {/* Map Modal */}
      {
        showMapModal && (
          <>
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 100000,
                padding: '20px',
                // backgroundColor: '#F8F8F8',

              }}
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setShowMapModal(false);
                }
              }}
            >
              <div
                style={{
                  backgroundColor: '#fff',
                  borderRadius: '20px',
                  width: '100%',
                  maxWidth: '1000px',
                  height: 'calc(100vh - 250px)',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                }}
              >
                {/* Modal Header */}
                <div className='relative'>
                  <div>
                    <div className='border-0  ' style={{ position: 'absolute', top: '10px', left: '20px', zIndex: '999', width: '100%' }}>
                      <div style={{ position: "relative", width: "70%" }}>

                        <input
                          className="border-0"
                          ref={mapSearchInputRef}
                          type="text"
                          value={mapSearchValue}
                          onChange={(e) => {
                            setMapSearchValue(e.target.value);
                            setShowMapPredictions(true);
                          }}
                          onFocus={() => {
                            if (mapPredictions.length > 0) {
                              setShowMapPredictions(true);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              e.stopPropagation();
                              if (mapPredictions.length > 0) {
                                handleMapPredictionSelect(mapPredictions[0]);
                              }
                            }
                          }}
                          placeholder="Type to search..."
                          autoComplete="off"
                          style={{
                            width: "100%",
                            padding: "10px 80px 10px 12px", // <-- extra space for icons
                            borderRadius: "6px",
                            fontWeight: "400",
                            fontSize: "16px",
                            color: "#292929e6",
                            outline: "none",
                            background: "#F8F8F8",
                            boxShadow: "0px 6px 24px 0px #00000029",
                          }}
                        />
                        {showMapPredictions && mapPredictions.length > 0 && (
                          <div
                            style={{
                              position: 'absolute',
                              top: 'calc(100% + 8px)',
                              left: 0,
                              width: '100%',
                              maxHeight: '260px',
                              overflowY: 'auto',
                              background: '#FFFFFF',
                              borderRadius: '14px',
                              boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.16)',
                              zIndex: 100002,
                              padding: '8px 0',
                            }}
                          >
                            {mapPredictions.map((prediction) => (
                              <button
                                key={prediction.place_id}
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handleMapPredictionSelect(prediction)}
                                style={{
                                  width: '100%',
                                  border: 'none',
                                  background: 'transparent',
                                  textAlign: 'left',
                                  padding: '10px 14px',
                                  color: '#1E293B',
                                  fontSize: '14px',
                                  lineHeight: 1.4,
                                  cursor: 'pointer',
                                }}
                              >
                                {prediction.description}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* ❌ Clear Button */}
                        {mapSearchValue && (
                          <span
                            onClick={() => {
                              setMapSearchValue("");
                              setMapPredictions([]);
                              setShowMapPredictions(false);
                            }}
                            style={{
                              position: "absolute",
                              right: "45px",        // <-- FIRST icon position
                              top: "50%",
                              transform: "translateY(-50%)",
                              cursor: "pointer",
                              fontSize: "20px",
                              color: "#777",
                              fontWeight: "bold",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#7f7f7f61",
                              borderRadius: "50%",
                              width: "30px",
                              height: "30px",
                              
                            }}
                          >
                            ×
                          </span>
                        )}

                        {/* 🔍 Your existing SVG icon */}
                        <svg
                          style={{
                            position: "absolute",
                            right: "10px",          // <-- SECOND icon position
                            top: "50%",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                          }}
                          width="22"
                          height="23"
                          viewBox="0 0 22 23"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M17.7038 16.6914C18.3025 15.5772 18.5268 14.3 18.3435 13.0485C18.1602 11.797 17.5791 10.6376 16.686 9.74193C16.6759 9.73143 16.6643 9.7228 16.6538 9.71268V3.0118C16.6538 2.94208 16.6343 2.87373 16.5976 2.81444C16.5609 2.75516 16.5084 2.70728 16.446 2.67618L11.1446 0.0391779C11.1353 0.0346779 11.1251 0.0331779 11.1154 0.0294279C11.1007 0.0229425 11.0855 0.0175528 11.07 0.0133029C11.0555 0.00989464 11.0409 0.00738981 11.0261 0.00580294C11.0094 0.00329557 10.9925 0.00191749 10.9755 0.00167797C10.9616 0.00185491 10.9477 0.00298256 10.9339 0.00505299C10.9165 0.00671126 10.8994 0.00972038 10.8825 0.014053C10.8684 0.0179714 10.8547 0.0228581 10.8413 0.028678C10.8307 0.0316133 10.8203 0.0351184 10.8101 0.0391779L5.67638 2.59293L0.541879 0.0391779C0.484578 0.0107039 0.420945 -0.00263485 0.357034 0.000430695C0.293122 0.00349624 0.231058 0.0228641 0.176746 0.0566917C0.122434 0.0905192 0.0776805 0.137681 0.0467442 0.193691C0.0158079 0.2497 -0.000282541 0.312693 3.75475e-06 0.376678V15.3309C-0.000309137 15.401 0.0190243 15.4698 0.055811 15.5294C0.0925977 15.5891 0.145365 15.6372 0.208129 15.6684L5.50913 18.3017C5.51475 18.3043 5.52113 18.3017 5.52713 18.3058C5.57375 18.3289 5.62509 18.341 5.67713 18.341C5.72917 18.341 5.78051 18.3289 5.82713 18.3058C5.83275 18.3032 5.83875 18.3058 5.84438 18.3017L7.767 17.3473C8.60153 18.5025 9.8275 19.3151 11.2166 19.6336C12.6056 19.9521 14.0631 19.7549 15.3176 19.0787L17.7255 21.8379C17.9006 22.0378 18.1148 22.1998 18.3548 22.314C18.5948 22.4281 18.8557 22.492 19.1213 22.5017H19.1846C19.6989 22.501 20.1919 22.2967 20.556 21.9336C20.7452 21.7463 20.8935 21.5218 20.9915 21.2742C21.0894 21.0267 21.1348 20.7615 21.1249 20.4954C21.1157 20.2298 21.0523 19.9689 20.9385 19.7287C20.8246 19.4885 20.6628 19.2742 20.463 19.0989L17.7038 16.6914ZM15.9038 9.09168C15.2492 8.63211 14.508 8.31065 13.7251 8.14689C12.9423 7.98313 12.1344 7.98051 11.3505 8.13918V0.981928L15.9019 3.2443L15.9038 9.09168ZM0.750004 0.981928L5.30138 3.2443V17.3608L0.750004 15.0984V0.981928ZM6.05138 17.3616V3.2443L10.6005 0.981928V8.34955C9.79235 8.62657 9.0545 9.07683 8.43856 9.66884C7.82263 10.2608 7.3435 10.9803 7.03471 11.7768C6.72591 12.5734 6.5949 13.4278 6.65086 14.2803C6.70681 15.1327 6.94837 15.9627 7.35863 16.7121L6.05138 17.3616ZM8.89613 17.5318C8.1782 16.8139 7.68929 15.8992 7.49121 14.9034C7.29313 13.9077 7.39478 12.8755 7.78331 11.9375C8.17184 10.9995 8.8298 10.1978 9.67398 9.63369C10.5182 9.06962 11.5107 8.76855 12.5259 8.76855C13.5412 8.76855 14.5337 9.06962 15.3779 9.63369C16.2221 10.1978 16.88 10.9995 17.2686 11.9375C17.6571 12.8755 17.7588 13.9077 17.5607 14.9034C17.3626 15.8992 16.8737 16.8139 16.1558 17.5318C15.1922 18.4928 13.8868 19.0325 12.5259 19.0325C11.165 19.0325 9.8597 18.4928 8.89613 17.5318ZM20.0258 21.4033C19.9115 21.5195 19.7742 21.6106 19.6227 21.6706C19.4712 21.7306 19.3088 21.7582 19.146 21.7517C18.9826 21.7468 18.822 21.7081 18.6743 21.638C18.5266 21.5679 18.3951 21.468 18.288 21.3444L15.9566 18.6733C16.4788 18.3084 16.9326 17.8543 17.2973 17.3319L19.968 19.6633C20.091 19.7707 20.1907 19.9022 20.2607 20.0497C20.3307 20.1973 20.3696 20.3576 20.375 20.5208C20.3803 20.684 20.3521 20.8466 20.2919 20.9984C20.2318 21.1502 20.1414 21.2881 20.0258 21.4033Z" fill="black" />
                          <path d="M15.4441 10.9835C14.867 10.4072 14.132 10.0149 13.332 9.85619C12.5319 9.69749 11.7028 9.77951 10.9494 10.0919C10.196 10.4043 9.5521 10.933 9.09909 11.6112C8.64608 12.2894 8.4043 13.0867 8.4043 13.9023C8.4043 14.718 8.64608 15.5153 9.09909 16.1935C9.5521 16.8717 10.196 17.4004 10.9494 17.7128C11.7028 18.0252 12.5319 18.1072 13.332 17.9485C14.132 17.7898 14.867 17.3975 15.4441 16.8212C16.2169 16.0463 16.6509 14.9967 16.6509 13.9023C16.6509 12.808 16.2169 11.7584 15.4441 10.9835ZM14.9124 16.2909C14.4401 16.7623 13.8388 17.0832 13.1842 17.2129C12.5297 17.3427 11.8514 17.2755 11.235 17.0199C10.6187 16.7643 10.0919 16.3317 9.72134 15.7768C9.35075 15.2219 9.15296 14.5696 9.15296 13.9023C9.15296 13.2351 9.35075 12.5828 9.72134 12.0279C10.0919 11.473 10.6187 11.0404 11.235 10.7848C11.8514 10.5292 12.5297 10.462 13.1842 10.5918C13.8388 10.7215 14.4401 11.0424 14.9124 11.5138C15.5447 12.1479 15.8999 13.0068 15.8999 13.9023C15.8999 14.7979 15.5447 15.6568 14.9124 16.2909Z" fill="black" />

                        </svg>

                      </div>



                      <div className='basic_detail_sumbit current_location mt-3'>
                        <Button
                          variant='outline-success' // Changed variant for visual distinction
                          onClick={handleUseCurrentLocationFromMap}
                        >
                          <PiArrowElbowUpRightFill /> Use Current Location
                        </Button>
                      </div>

                    </div>
                    <div style={{ position: 'absolute', right: '20px', top: '20px', zIndex: '999' }}>
                      <button
                        type="button"
                        onClick={() => {
                          cleanupMapSearch();
                          setShowMapModal(false);
                        }}
                        style={{
                          background: '#FFFFFF',
                          boxShadow: '0px 6px 20px 0px #00000029',
                          fontSize: '20px',
                          cursor: 'pointer',
                          padding: '0',
                          width: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50px',
                          color: '#191919CC'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FFFFFF'}
                      >
                        <IoCloseOutline />
                      </button>
                    </div>
                  </div>


                  {/* Map Container */}
                  <div
                    id="map-container"
                    style={{
                      flex: 1,
                      height: 'calc(100vh - 250px)',
                      position: 'relative',
                      borderRadius: '20px'
                    }}
                  />

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '12px',
                      position: 'absolute',
                      bottom: '20px',
                      left: '20px'
                    }}
                  >
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {/* <button
                      type="button"
                      onClick={() => {
                        cleanupMapSearch();
                        setShowMapModal(false);
                      }}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: '#fff',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 500
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                    >
                      Cancel
                    </button> */}
                      {/* <button
                      type="button"
                      onClick={handleConfirmLocationWithCleanup}
                      style={{
                        padding: '8px 16px',
                        border: 'none',
                        borderRadius: '4px',
                        backgroundColor: '#0066cc',
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 500
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0052a3'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0066cc'}
                    >
                      Confirm Location
                    </button> */}
                      {/* <div className='Back_To_Login basic_detail_sumbit'>
                      <Button variant="outline-primary" className=' text-decoration-none'
                        onClick={() => {
                          cleanupMapSearch();
                          setShowMapModal(false);
                        }}>
                        Cancel
                      </Button>
                    </div> */}
                      <div className="basic_detail_sumbit">
                        <Button
                          variant='primary'
                          // type="submit"
                          onClick={handleConfirmLocationWithCleanup}
                        >
                          Confirm Location
                        </Button>
                        <div className='basic_detail_sumbit'>
                          {/* <Button
                          variant='outline-success' 
                          onClick={handleUseCurrentLocationFromMap}
                        >
                          Use Current Location zxgg📍
                        </Button> */}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </>
        )
      }
    </>
  );
};

export default GooglePlacesAutocomplete;
